import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Formik, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import FormField from '../../../FormField';
import { useAutocomplete } from '../../../../hooks/useAutocomplete';
import { GET_SKILLS_BY_FILTER_QUERY } from '../../../../graphql/GET_SKILLS_BY_FILTER_QUERY';
import { GET_PROFESSIONS_BY_FILTER_QUERY } from '../../../../graphql/GET_PROFESSIONS_BY_FILTER_QUERY';
import AsyncCreatableSelect from 'react-select/async-creatable';
import Select from 'react-select';
import * as projectConstants from '../constants';
import { validVideoUrlRegex } from '../../../../services/videoUrlParser';
import { ProjectFormValues } from '../ProjectFlow';
import editIcon from '../../../../assets/svg/landing/icon-pen.svg';
import mascotHappy from '../../../../assets/svg/onboarding/mascot-happy.svg';
import searchIcon from '../../../../assets/svg/search.svg';

import globeGrey from '../../../../assets/images/project/globe-grey.svg';
import globeRed from '../../../../assets/images/project/globe-red.svg';
import usersGrey from '../../../../assets/images/project/users-grey.svg';
import usersRed from '../../../../assets/images/project/users-red.svg';
import heartGrey from '../../../../assets/svg/heart-grey.svg';
import heartRed from '../../../../assets/svg/heart-red.svg';
import coinGrey from '../../../../assets/svg/coin-grey.svg';
import coinWhite from '../../../../assets/svg/coin-white.svg';

interface ProjectDetailsStepProps {
    initialValues: ProjectFormValues;
    onComplete: (values: ProjectFormValues) => void;
    onBack: () => void;
}

const PLACEHOLDERS = [
    'Looking for an editor for a ninja action project',
    'Looking for a 3D artist for a music album',
    'Looking for a game designer for a sci-fi universe'
];
const STORAGE_KEY = 'matchhat_project_placeholder_idx';

const projectValidationSchema = Yup.object().shape({
    description: Yup.string().required('This field is required'),
    title: Yup.string().required('This field is required'),
    type: Yup.string().required('This field is required'),
    shareWith: Yup.string().required('This field is required'),
    videoUrl: Yup.string()
        .matches(
            validVideoUrlRegex,
            'Input must be a valid Youtube or Vimeo video URL.'
        )
        .nullable()
});

const ProjectDetailsStep: React.FC<ProjectDetailsStepProps> = ({
    initialValues,
    onComplete,
    onBack
}) => {
    const [descriptionPlaceholder] = useState<string>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            const parsed = stored !== null ? parseInt(stored, 10) : 0;
            const currentIdx = isNaN(parsed) ? 0 : parsed % PLACEHOLDERS.length;
            localStorage.setItem(STORAGE_KEY, String((currentIdx + 1) % PLACEHOLDERS.length));
            return PLACEHOLDERS[currentIdx];
        } catch {
            return PLACEHOLDERS[0];
        }
    });

    useEffect(() => {
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'auto' });
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        }, 100);
    }, []);
    const { loadOptions } = useAutocomplete({
        query: GET_SKILLS_BY_FILTER_QUERY
    });

    const { loadOptions: loadProfessionOptions } = useAutocomplete({
        query: GET_PROFESSIONS_BY_FILTER_QUERY
    });

    const [genreSearchInput, setGenreSearchInput] = useState('');
    const [showGenreDropdown, setShowGenreDropdown] = useState(false);
    const [showFundingDropdown, setShowFundingDropdown] = useState(false);
    const genreInputRef = useRef<HTMLInputElement>(null);
    const genreDropdownRef = useRef<HTMLDivElement>(null);
    const fundingDropdownRef = useRef<HTMLDivElement>(null);
    const fundingButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                genreDropdownRef.current &&
                !genreDropdownRef.current.contains(event.target as Node) &&
                genreInputRef.current &&
                !genreInputRef.current.contains(event.target as Node)
            ) {
                setShowGenreDropdown(false);
            }

            if (
                fundingDropdownRef.current &&
                !fundingDropdownRef.current.contains(event.target as Node) &&
                fundingButtonRef.current &&
                !fundingButtonRef.current.contains(event.target as Node)
            ) {
                setShowFundingDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <Formik
            validationSchema={projectValidationSchema}
            initialValues={initialValues}
            onSubmit={(values) => {
                onComplete(values);
            }}
            enableReinitialize
        >
            {({
                values,
                handleChange,
                handleBlur,
                handleSubmit,
                setFieldValue,
                errors,
                touched
            }) => {
                const filteredGenreOptions = genreSearchInput
                    ? projectConstants.projectGenreOptions.filter((genre) =>
                          genre.label.toLowerCase().includes(genreSearchInput.toLowerCase())
                      )
                    : projectConstants.projectGenreOptions;

                const availableGenreOptions = filteredGenreOptions.filter(
                    (genre) => !values.genres.includes(genre.value)
                );

                const exactMatch = projectConstants.projectGenreOptions.some(
                    (genre) => genre.label.toLowerCase() === genreSearchInput.trim().toLowerCase()
                );
                const showCreateOption = genreSearchInput.trim() !== '' && !exactMatch && !values.genres.includes(genreSearchInput.trim());

                return (
                <form onSubmit={handleSubmit}>
                    <div className="columns section page-add-project-container no-bottom-margin">
                        <div className="column">
                            <div className="project-review-header is-rounded is-dropshadowed">
                                <div className="review-progress-bar-container">
                                    <div className="review-progress-bar width-50" />
                                </div>
                                <div className="review-progress-indicator">
                                    1/2 Completed
                                </div>
                                <div className="project-review-header-content">
                                    <div className="project-review-header-left">
                                        <h1>Your Project is Ready for Review!</h1>
                                        <p>Review or change your project information</p>
                                    </div>
                                    <div className="project-review-header-right">
                                        <img src={mascotHappy} />
                                    </div>
                                </div>
                            </div>

                            <div className="card page-add-project-card is-rounded is-dropshadowed">
                                <div className="card-content page-add-project-card-content">
                                    <div className="project-title-section">
                                        <h2 className="project-dynamic-title">
                                            {values.title || 'Your Project'}
                                        </h2>
                                        <button
                                            type="button"
                                            className="project-edit-button-circle"
                                            onClick={() => {
                                                const titleInput = document.querySelector('input[name="title"]') as HTMLInputElement;
                                                titleInput?.focus();
                                            }}
                                        >
                                            <img src={editIcon} alt="Edit" />
                                        </button>
                                    </div>

                                    {/* Project Title */}
                                    <div className="field mb-5">
                                        <label className="label">
                                            Project Title  <span className="has-text-danger">*</span>
                                        </label>
                                        <div className="control">
                                            <FormField
                                                name="title"
                                                type="text"
                                                placeholder="Japanese horror film"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.title}
                                                additionalInputClasses="has-background-white"
                                            />
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="field mb-5">
                                        <label className="label">Description  <span className="has-text-danger">*</span></label>
                                        <div className="control">
                                            <FormField
                                                multiline={true}
                                                autoResize={true}
                                                name="description"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder={descriptionPlaceholder}
                                                value={values.description}
                                                additionalInputClasses="has-background-white"
                                            />
                                        </div>
                                    </div>

                                    {/* Type of Project */}
                                    <div className="field mb-5">
                                        <label className="label">
                                            Type  <span className="has-text-danger">*</span>
                                        </label>
                                        <div className="control switchradio is-flex-desktop is-block-mobile">
                                            {projectConstants.projectTypeOptions.map((projectType) => (
                                                <Fragment key={projectType.value}>
                                                    <input
                                                        type="radio"
                                                        value={projectType.value}
                                                        id={projectType.value}
                                                        name="type"
                                                        className="switchradio-input is-absolute"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        checked={values.type === projectType.value}
                                                        required={true}
                                                    />
                                                    <label
                                                        htmlFor={projectType.value}
                                                        className="button has-text-weight-bold switchradio-label p-t-1 p-b-1"
                                                        key={projectType.value}
                                                    >
                                                        <img
                                                            src={
                                                                projectType.value === 'PASSION'
                                                                    ? values.type === projectType.value
                                                                        ? heartRed
                                                                        : heartGrey
                                                                    : values.type === projectType.value
                                                                        ? coinWhite
                                                                        : coinGrey
                                                            }
                                                            alt=""
                                                            className="is-inline image"
                                                        />
                                                        <span className="is-small">
                                                            {projectType.label}
                                                        </span>
                                                    </label>
                                                </Fragment>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Share With */}
                                    <div className="field mb-5">
                                        <label className="label">Share With  <span className="has-text-danger">*</span></label>
                                        <div className="control switchradio is-flex-desktop is-block-mobile">
                                            {projectConstants.projectShareableOptions.map(
                                                (shareableStatus) => (
                                                    <Fragment key={shareableStatus.value}>
                                                        <Field
                                                            type="radio"
                                                            value={shareableStatus.value}
                                                            id={shareableStatus.value}
                                                            name="shareWith"
                                                            className="switchradio-input is-absolute"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            checked={values.shareWith === shareableStatus.value}
                                                            required={true}
                                                        />
                                                        <label
                                                            htmlFor={shareableStatus.value}
                                                            className="button has-text-weight-bold switchradio-label p-t-1 p-b-1"
                                                            key={shareableStatus.value}
                                                        >
                                                            <img
                                                                src={
                                                                    shareableStatus.value === 'PUBLIC'
                                                                        ? values.shareWith === shareableStatus.value
                                                                            ? globeRed
                                                                            : globeGrey
                                                                        : values.shareWith === shareableStatus.value
                                                                            ? usersRed
                                                                            : usersGrey
                                                                }
                                                                alt=""
                                                                className="is-inline image"
                                                            />
                                                            <span className="is-small">
                                                                {shareableStatus.label}
                                                            </span>
                                                        </label>
                                                    </Fragment>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {/* I'm Looking For (Collaborators) */}
                                    <div className="field mb-5">
                                        <label className="label">I'm Looking For</label>
                                        <FieldArray name="collaborations">
                                            {({ push, remove }) => (
                                                <div className="collaborations-section mt-3">
                                                    {values.collaborations && values.collaborations.length > 0 && values.collaborations.map((collab: any, index: number) => (
                                                        <div key={index} className="box mb-4 is-shadowless" style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1.25rem', backgroundColor: '#F9FAFB' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                                <span style={{ color: '#374151' }}>Collaborator {index + 1}</span>
                                                                <span
                                                                    className="is-clickable"
                                                                    style={{ color: '#EF4444', cursor: 'pointer' }}
                                                                    onClick={() => remove(index)}
                                                                >
                                                                    Remove
                                                                </span>
                                                            </div>
                                                            <style>{`
                                                                @media (max-width: 768px) {
                                                                    .custom-mobile-col-top { margin-bottom: 0.75rem !important; }
                                                                    .custom-mobile-col-bottom { padding-top: 0 !important; }
                                                                }
                                                            `}</style>
                                                            <div className="columns" style={{ marginBottom: 0 }}>
                                                                <div className="column is-half custom-mobile-col-top" style={{ paddingBottom: 0 }}>
                                                                    <div className="field" style={{ marginBottom: 0 }}>
                                                                        <label className="label">Profession</label>
                                                                        <div className="control">
                                                                            <AsyncCreatableSelect
                                                                                cacheOptions={true}
                                                                                isClearable={true}
                                                                                loadOptions={loadProfessionOptions}
                                                                                placeholder="Designer"
                                                                                onChange={(newValue: any) => {
                                                                                    setFieldValue(`collaborations.${index}.professions[0]`, newValue ? newValue.label : '');
                                                                                }}
                                                                                value={
                                                                                    collab.professions && collab.professions[0]
                                                                                        ? { label: collab.professions[0], value: collab.professions[0] }
                                                                                        : null
                                                                                }
                                                                                styles={{
                                                                                    control: (base) => ({
                                                                                        ...base,
                                                                                        borderColor: '#E5E7EB',
                                                                                        boxShadow: 'none',
                                                                                        fontSize: '14px'
                                                                                    })
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="column is-half custom-mobile-col-bottom" style={{ paddingBottom: 0 }}>
                                                                    <div className="field" style={{ marginBottom: 0 }}>
                                                                        <label className="label">Compensation</label>
                                                                        <div className="control">
                                                                            <Select
                                                                                options={projectConstants.collaboratorCompensationTypeOptions}
                                                                                value={projectConstants.collaboratorCompensationTypeOptions.find(opt => opt.value === values.collaborations[index].compensation)}
                                                                                onChange={(option: any) => { setFieldValue(`collaborations.${index}.compensation`, option.value); }}
                                                                                styles={{
                                                                                    control: (base) => ({
                                                                                        ...base,
                                                                                        borderColor: '#E5E7EB',
                                                                                        boxShadow: 'none',
                                                                                        fontSize: '14px'
                                                                                    })
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="field" style={{ marginTop: '0.75rem' }}>
                                                                <label className="label">Profession You Are Looking For</label>
                                                                <div className="control">
                                                                    <Field
                                                                        name={`collaborations.${index}.additionalRequirements`}
                                                                        className="input has-background-white"
                                                                        placeholder="Designer"
                                                                        style={{ borderColor: '#E5E7EB', boxShadow: 'none' }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <div
                                                        className="is-clickable has-text-weight-semibold mb-2 mt-1"
                                                        style={{ color: '#5775FF', cursor: 'pointer' }}
                                                        onClick={() => push({ professions: [''], additionalRequirements: '', compensation: 'UNPAID' })}
                                                    >
                                                        + Add collaborator
                                                    </div>
                                                </div>
                                            )}
                                        </FieldArray>
                                    </div>

                                    {/* You're Looking For */}
                                    <div className="field mb-5">
                                        <label className="label">You're Looking For</label>
                                        <div className="control">
                                            <SearchTagInput
                                                selectedValues={values.requiredSkills}
                                                allOptions={[]}
                                                onAdd={async (skill) => {
                                                    if (!values.requiredSkills.includes(skill)) {
                                                        await setFieldValue('requiredSkills', [...values.requiredSkills, skill]);
                                                    }
                                                }}
                                                onRemove={async (skill) => await setFieldValue('requiredSkills', values.requiredSkills.filter(s => s !== skill))}
                                                placeholder="Search to add"
                                                loadOptions={loadOptions}
                                                minSearchLength={2}
                                                allowCreate={true}
                                            />
                                        </div>
                                    </div>

                                    {/* Country */}
                                    <div className="field mb-5">
                                        <label className="label">Country</label>
                                        <div className="control">
                                            <input
                                                type="text"
                                                className="input has-background-white"
                                                name="country"
                                                placeholder="Enter your country"
                                                value={values.country}
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    if (!e.target.value) {
                                                        setFieldValue('onlyCreatorsBasedInLocation', false);
                                                    }
                                                }}
                                                onBlur={handleBlur}
                                            />
                                        </div>
                                        <div className="field mt-4">
                                            <label className={`checkbox${!values.country ? ' is-disabled' : ''}`}>
                                                <Field
                                                    type="checkbox"
                                                    name="onlyCreatorsBasedInLocation"
                                                    checked={values.onlyCreatorsBasedInLocation}
                                                    onChange={handleChange}
                                                    disabled={!values.country}
                                                />
                                                <span className="ml-2">
                                                    Only creators based in {values.country || 'this country'}
                                                </span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Genre */}
                                    <div className="field mb-5">
                                        <label className="label">Genre</label>
                                        <div className="project-tags-input-container">
                                            <div className="selected-tags-wrapper">
                                                {values.genres &&
                                                    values.genres.length > 0 &&
                                                    values.genres.map((genreValue) => {
                                                        const genreOption =
                                                            projectConstants.projectGenreOptions.find(
                                                                (g) => g.value === genreValue
                                                            );
                                                        return (
                                                            <div
                                                                key={genreValue}
                                                                className="selected-tag-chip"
                                                            >
                                                                <span className="tag-text">
                                                                    {genreOption
                                                                        ? genreOption.label
                                                                        : genreValue}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    className="tag-remove-btn"
                                                                    onClick={() => {
                                                                        const updatedGenres =
                                                                            values.genres.filter(
                                                                                (g) => g !== genreValue
                                                                            );
                                                                        setFieldValue('genres', updatedGenres);
                                                                    }}
                                                                >
                                                                    ×
                                                                </button>
                                                            </div>
                                                        );
                                                    })}

                                                <div className="tag-search-input-wrapper">
                                                    <img
                                                        src={searchIcon}
                                                        alt="Search"
                                                        className="tag-search-icon"
                                                    />
                                                    <input
                                                        ref={genreInputRef}
                                                        type="text"
                                                        className="tag-search-input"
                                                        placeholder="Search genres"
                                                        value={genreSearchInput}
                                                        onChange={(e) => {
                                                            setGenreSearchInput(e.target.value);
                                                            setShowGenreDropdown(true);
                                                        }}
                                                        onFocus={() => setShowGenreDropdown(true)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                let newGenre = '';
                                                                if (availableGenreOptions.length > 0) {
                                                                    newGenre = availableGenreOptions[0].value;
                                                                } else if (genreSearchInput.trim() !== '') {
                                                                    newGenre = genreSearchInput.trim();
                                                                }

                                                                if (newGenre && !values.genres.includes(newGenre)) {
                                                                    const updatedGenres = [
                                                                        ...values.genres,
                                                                        newGenre
                                                                    ];
                                                                    setFieldValue('genres', updatedGenres);
                                                                    setGenreSearchInput('');
                                                                    setShowGenreDropdown(false);
                                                                }
                                                            } else if (e.key === 'Escape') {
                                                                setShowGenreDropdown(false);
                                                            }
                                                        }}
                                                    />
                                                </div>

                                                {showGenreDropdown &&
                                                    genreSearchInput &&
                                                    (availableGenreOptions.length > 0 || showCreateOption) && (
                                                        <div
                                                            ref={genreDropdownRef}
                                                            className="genre-dropdown"
                                                        >
                                                            {availableGenreOptions.map((genre) => (
                                                                <div
                                                                    key={genre.value}
                                                                    className="genre-dropdown-item"
                                                                    onClick={() => {
                                                                        const updatedGenres = [
                                                                            ...values.genres,
                                                                            genre.value
                                                                        ];
                                                                        setFieldValue('genres', updatedGenres);
                                                                        setGenreSearchInput('');
                                                                        setShowGenreDropdown(false);
                                                                        genreInputRef.current?.focus();
                                                                    }}
                                                                >
                                                                    {genre.label}
                                                                </div>
                                                            ))}
                                                            {showCreateOption && (
                                                                <div
                                                                    className="genre-dropdown-item"
                                                                    onClick={() => {
                                                                        const newGenre = genreSearchInput.trim();
                                                                        const updatedGenres = [
                                                                            ...values.genres,
                                                                            newGenre
                                                                        ];
                                                                        setFieldValue('genres', updatedGenres);
                                                                        setGenreSearchInput('');
                                                                        setShowGenreDropdown(false);
                                                                        genreInputRef.current?.focus();
                                                                    }}
                                                                >
                                                                    Create "{genreSearchInput.trim()}"
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Video URL */}
                                    <div className="field mb-5">
                                        <label className="label">Video URL</label>
                                        <div className="control">
                                            <FormField
                                                name="videoUrl"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter URL"
                                                value={values.videoUrl}
                                                additionalInputClasses="has-background-white"
                                            />
                                        </div>
                                    </div>

                                    {/* Funding */}
                                    <div className="field mb-5 funding-field-spacing">
                                        <label className="label">
                                            Funding
                                        </label>
                                        <div className="control funding-control">
                                            <button
                                                ref={fundingButtonRef}
                                                type="button"
                                                className="funding-button"
                                                onClick={() =>
                                                    setShowFundingDropdown(!showFundingDropdown)
                                                }
                                            >
                                                <span>
                                                    {projectConstants.projectFundingStatusOptions.find(
                                                        (f) => f.value === values.fundingStatus
                                                    )?.label ?? 'Select funding status'}
                                                </span>
                                                <span
                                                    className={`funding-button-arrow ${
                                                        showFundingDropdown ? 'is-open' : ''
                                                    }`}
                                                >
                                                    ▼
                                                </span>
                                            </button>

                                            {showFundingDropdown && (
                                                <div
                                                    ref={fundingDropdownRef}
                                                    className="funding-dropdown"
                                                    style={{
                                                        top: fundingButtonRef.current
                                                            ? `${
                                                                  fundingButtonRef.current.getBoundingClientRect()
                                                                      .bottom + 8
                                                              }px`
                                                            : 'auto',
                                                        left: fundingButtonRef.current
                                                            ? `${
                                                                  fundingButtonRef.current.getBoundingClientRect()
                                                                      .left
                                                              }px`
                                                            : 'auto',
                                                        width: fundingButtonRef.current
                                                            ? `${
                                                                  fundingButtonRef.current.getBoundingClientRect()
                                                                      .width
                                                              }px`
                                                            : 'auto'
                                                    }}
                                                >
                                                    {projectConstants.projectFundingStatusOptions.map(
                                                        (funding) => {
                                                            const isSelected =
                                                                values.fundingStatus === funding.value;
                                                            return (
                                                                <div
                                                                    key={funding.value}
                                                                    className={`funding-dropdown-item ${
                                                                        isSelected ? 'is-selected' : ''
                                                                    }`}
                                                                    onClick={() => {
                                                                        setFieldValue(
                                                                            'fundingStatus',
                                                                            funding.value
                                                                        );
                                                                        setShowFundingDropdown(false);
                                                                    }}
                                                                >
                                                                    {isSelected && (
                                                                        <span className="dropdown-checkmark">
                                                                            ✓
                                                                        </span>
                                                                    )}
                                                                    <span>{funding.label}</span>
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Footer with Next button */}
                                <div className="page-add-project-controls">
                                    <div className="level container page-add-project-controls-container">
                                        <div className="level-left page-add-project-controls-container-level" />
                                        <div className="level-right page-add-project-controls-container-level">
                                            <button
                                                type="submit"
                                                className="button is-fullwidth-mobile page-add-project-controls-button has-text-weight-semibold is-primary"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
                );
            }}
        </Formik>
    );
};

interface SearchTagInputProps {
    selectedValues: string[];
    allOptions: Array<{ id: string; name: string }>;
    onAdd: (id: string) => void;
    onRemove: (id: string) => void;
    formatLabel?: (name: string) => string;
    placeholder?: string;
    loadOptions?: (searchTerm: string) => Promise<any[]>;
    minSearchLength?: number;
    allowCreate?: boolean;
}

const SearchTagInput: React.FC<SearchTagInputProps> = ({
    selectedValues,
    allOptions,
    onAdd,
    onRemove,
    formatLabel = (name) => name,
    placeholder = 'Search to add',
    loadOptions,
    minSearchLength = 1,
    allowCreate = false
}) => {
    const [searchInput, setSearchInput] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [asyncOptions, setAsyncOptions] = useState<Array<{ id: string; name: string }>>([]);
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (loadOptions && searchInput.length >= minSearchLength) {
            setIsLoading(true);
            loadOptions(searchInput).then((options: any[]) => {
                const formatted = options?.map((opt: any) => ({
                    id: opt.value || opt.label,
                    name: opt.label
                })) || [];
                setAsyncOptions(formatted);
                setIsLoading(false);
            }).catch(() => {
                setIsLoading(false);
            });
        } else {
            setAsyncOptions([]);
        }
    }, [searchInput, loadOptions, minSearchLength]);

    const effectiveOptions = loadOptions ? asyncOptions : allOptions;

    const filteredOptions = effectiveOptions
        .filter(opt => opt.name.toLowerCase().includes(searchInput.toLowerCase()))
        .filter(opt => !selectedValues.includes(opt.id))
        .slice(0, 5);

    const exactMatch = effectiveOptions.some(
        (opt) => opt.name.toLowerCase() === searchInput.trim().toLowerCase()
    );
    const showCreateOption = allowCreate && searchInput.trim() !== '' && !exactMatch && !selectedValues.some(v => v.toLowerCase() === searchInput.trim().toLowerCase());

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                inputRef.current && !inputRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="project-tags-input-container">
            <div className="selected-tags-wrapper">
                {selectedValues.map((valueId) => {
                    const option = effectiveOptions.find((o) => o.id === valueId || o.name === valueId);
                    return (
                        <div key={valueId} className="selected-tag-chip">
                            <span className="tag-text">
                                {option ? formatLabel(option.name) : valueId}
                            </span>
                            <button
                                type="button"
                                className="tag-remove-btn"
                                onClick={() => onRemove(valueId)}
                            >
                                ×
                            </button>
                        </div>
                    );
                })}

                <div className="tag-search-input-wrapper">
                    <img src={searchIcon} alt="Search" className="tag-search-icon" />
                    <input
                        ref={inputRef}
                        type="text"
                        className="tag-search-input"
                        placeholder={placeholder}
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (filteredOptions.length > 0) {
                                    onAdd(loadOptions ? filteredOptions[0].name : filteredOptions[0].id);
                                    setSearchInput('');
                                    setShowDropdown(false);
                                } else if (showCreateOption) {
                                    onAdd(searchInput.trim());
                                    setSearchInput('');
                                    setShowDropdown(false);
                                }
                            } else if (e.key === 'Escape') {
                                setShowDropdown(false);
                            }
                        }}
                    />
                </div>

                {isLoading && searchInput && (
                    <div ref={dropdownRef} className="genre-dropdown">
                        <div className="genre-dropdown-item">Loading...</div>
                    </div>
                )}

                {!isLoading && showDropdown && searchInput && (filteredOptions.length > 0 || showCreateOption) && (
                    <div ref={dropdownRef} className="genre-dropdown">
                        {filteredOptions.map((option) => (
                            <div
                                key={option.id}
                                className="genre-dropdown-item"
                                onClick={() => {
                                    onAdd(loadOptions ? option.name : option.id);
                                    setSearchInput('');
                                    setShowDropdown(false);
                                    inputRef.current?.focus();
                                }}
                            >
                                {formatLabel(option.name)}
                            </div>
                        ))}
                        {showCreateOption && (
                            <div
                                className="genre-dropdown-item"
                                onClick={() => {
                                    onAdd(searchInput.trim());
                                    setSearchInput('');
                                    setShowDropdown(false);
                                    inputRef.current?.focus();
                                }}
                            >
                                Create "{searchInput.trim()}"
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectDetailsStep;
